import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { Button } from '@/common/components/Button/Button';
import { Dialog } from '@/common/components/Dialog/Dialog';
import { Icon } from '@/common/components/Icons/Icon';
import { ROUTES } from '@/common/constants/routes';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { CoverflexAIStorage } from '@/common/integrations/coverflexai/coverflexai.storage';
import { AuthService } from '@/common/integrations/menhir/employee/sessions/sessions.service';
import { StorageService } from '@/common/integrations/storage/storage.service';
import { router } from 'expo-router';
import { CardAction } from '@/common/components/CardAction/CardAction';

export type TLogoutActionProps = {
  variant?: 'button' | 'card';
};

export function LogoutAction({ variant = 'button' }: TLogoutActionProps) {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleSignOut = async (forgetDevice2FA: boolean) => {
    await AuthService.signOut();
    if (forgetDevice2FA) {
      await StorageService.clearUserAgentToken();
      // TODO: Remove this once we have universal storage for CoverflexAI
      if (Platform.OS === 'web') {
        CoverflexAIStorage.deleteConversations();
      }
    }
    signOut();
    router.push(ROUTES.SIGNIN);
  };

  return (
    <>
      {variant === 'button' && (
        <Button
          variant="tertiary"
          icon={<Icon icon="right-from-bracket" />}
        onPress={async () => {
          if (!!(await StorageService.getUserAgentToken())) {
            setShowLogoutDialog(true);
          } else {
            AnalyticsService.trackCustomEvent('logout_button_clicked');
            handleSignOut(false);
          }
        }}
        size="sm"
      >
          {t('dashboard.profile.logout.label')}
        </Button>
      )}
      {variant === 'card' && (
        <CardAction
          icon={<Icon icon="right-from-bracket" />}
          title={t('dashboard.profile.logout.label')}
          onPress={() => setShowLogoutDialog(true)}
          action={<Icon icon="chevron-right" />}
        />
      )}
      {showLogoutDialog && (
        <Dialog
          icon={<Icon icon="lock" size={24} />}
          iconBadgeColor="accentSecondary20"
          onClose={() => setShowLogoutDialog(false)}
          secondaryAction={{
            children: t('dashboard.profile.logout.dialog.action.forget'),
            onPress: (closeDialog) => {
              AnalyticsService.trackCustomEvent(
                'logout_dialog_button_clicked',
                {
                  forget_device: true,
                }
              );
              handleSignOut(true);
              closeDialog();
            },
          }}
          primaryAction={{
            children: t('dashboard.profile.logout.dialog.action.trust'),
            onPress: (closeDialog) => {
              AnalyticsService.trackCustomEvent(
                'logout_dialog_button_clicked',
                {
                  forget_device: false,
                }
              );
              handleSignOut(false);
              closeDialog();
            },
          }}
          title={t('dashboard.profile.logout.dialog.title')}
          message={t('dashboard.profile.logout.dialog.description')}
        />
      )}
    </>
  );
}
